/* eslint-disable */
import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { X, Plus, AlertTriangle, CheckCircle, Circle, Send, Copy, Check, FileText } from 'lucide-react';
import { C, inp, lbl, fmt, fmtDT, genToken } from './teamUtils';

// ── Config ────────────────────────────────────────────────────────────
const WARNING_TYPES = {
  informal: {
    label:       'Informal Log',
    icon:        '📝',
    color:       C.mid,
    bg:          C.cream,
    border:      C.border,
    internal:    true,
    description: 'Internal note — not sent to employee',
  },
  verbal: {
    label:       'Verbal Warning',
    icon:        '🗣️',
    color:       C.amber,
    bg:          C.amberLight,
    border:      C.amber,
    internal:    false,
    description: 'Logged and emailed to employee for confirmation',
  },
  written: {
    label:       'Written Warning',
    icon:        '✉️',
    color:       C.orange,
    bg:          C.orangeLight,
    border:      C.orange,
    internal:    false,
    description: 'Formal written warning — emailed + PDF available',
  },
  final: {
    label:       'Final Warning',
    icon:        '⚠️',
    color:       C.red,
    bg:          C.redLight,
    border:      C.red,
    internal:    false,
    description: 'Last warning before disciplinary action',
  },
  pip: {
    label:       'Improvement Plan',
    icon:        '📋',
    color:       C.skyText,
    bg:          C.skyBlueLight,
    border:      C.skyText,
    internal:    false,
    description: 'Verbetertraject — structured improvement period',
  },
  outcome: {
    label:       'PIP Outcome',
    icon:        '🏁',
    color:       C.purple,
    bg:          C.purpleLight,
    border:      C.purple,
    internal:    true,
    description: 'Final outcome of improvement plan',
  },
};

const SEVERITY_CONFIG = {
  minor:    { label: 'Minor',    color: C.amber,  bg: C.amberLight  },
  moderate: { label: 'Moderate', color: C.orange, bg: C.orangeLight },
  serious:  { label: 'Serious',  color: C.red,    bg: C.redLight    },
};

const RECOGNITION_TYPES = {
  kudos: {
    label:       'Kudos',
    icon:        '📣',
    color:       C.green,
    bg:          C.greenLight,
    border:      C.green,
    description: 'Great shift, above & beyond, customer compliment',
  },
  commendation: {
    label:       'Formal Commendation',
    icon:        '⭐',
    color:       C.skyText,
    bg:          C.skyBlueLight,
    border:      C.skyText,
    description: 'Significant contribution or leadership shown',
  },
  tm_of_month: {
    label:       'TM of the Month',
    icon:        '🏆',
    color:       C.amber,
    bg:          C.amberLight,
    border:      C.amber,
    description: '€100 cash prize — one winner per calendar month',
  },
};

const RECOGNITION_CATEGORIES = [
  { value: 'service',    label: 'Customer Service' },
  { value: 'teamwork',   label: 'Team Player'      },
  { value: 'initiative', label: 'Initiative'        },
  { value: 'leadership', label: 'Leadership'        },
  { value: 'attitude',   label: 'Attitude'          },
  { value: 'other',      label: 'Other'             },
];

const OUTCOME_OPTIONS = [
  { value: 'improved',     label: '✅ Improved — issue resolved'       },
  { value: 'not_improved', label: '❌ Not improved — further action'   },
  { value: 'dismissed',    label: '🚫 Dismissed'                       },
  { value: 'resigned',     label: '🚪 Resigned during process'         },
];

// ── Type picker ───────────────────────────────────────────────────────
function TypePicker({ onSelect, onClose, category }) {
  const informal = ['informal'];
  const formal   = ['verbal', 'written', 'final', 'pip', 'outcome'];

  if (category === 'recognition') {
    return (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:700, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
        <div style={{ background:C.white, borderRadius:'16px', width:'100%', maxWidth:'480px', boxShadow:'0 24px 64px rgba(0,0,0,0.22)', overflow:'hidden' }}>

          <div style={{ padding:'18px 24px', background:C.navy, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div>
              <div style={{ fontWeight:700, fontSize:'15px', color:C.white }}>Recognition Entry</div>
              <div style={{ fontSize:'12px', color:C.skyBlue, marginTop:'2px' }}>Select entry type</div>
            </div>
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.12)', border:'none', borderRadius:'8px', cursor:'pointer', color:C.white, padding:'6px', display:'flex' }}><X size={18}/></button>
          </div>

          <div style={{ padding:'20px 24px', display:'flex', flexDirection:'column', gap:'8px' }}>
            {Object.entries(RECOGNITION_TYPES).map(([key, t]) => (
              <button key={key} onClick={() => onSelect(key)}
                style={{ display:'flex', alignItems:'center', gap:'12px', padding:'14px 16px', background:t.bg, border:`1px solid ${t.border}44`, borderRadius:'12px', cursor:'pointer', textAlign:'left', transition:'all 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = t.border; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = t.border + '44'; e.currentTarget.style.boxShadow = 'none'; }}>
                <span style={{ fontSize:'1.6rem', flexShrink:0 }}>{t.icon}</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:'13px', color:t.color }}>{t.label}</div>
                  <div style={{ fontSize:'11px', color:C.mid, marginTop:'2px' }}>{t.description}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:700, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:C.white, borderRadius:'16px', width:'100%', maxWidth:'480px', boxShadow:'0 24px 64px rgba(0,0,0,0.22)', overflow:'hidden' }}>

        <div style={{ padding:'18px 24px', background:C.navy, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontWeight:700, fontSize:'15px', color:C.white }}>Conduct Log</div>
            <div style={{ fontSize:'12px', color:C.skyBlue, marginTop:'2px' }}>Select entry type</div>
          </div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.12)', border:'none', borderRadius:'8px', cursor:'pointer', color:C.white, padding:'6px', display:'flex' }}><X size={18}/></button>
        </div>

        <div style={{ padding:'20px 24px', display:'flex', flexDirection:'column', gap:'16px' }}>

          {/* Informal */}
          <div>
            <div style={{ fontSize:'11px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'8px' }}>Internal</div>
            {informal.map(key => {
              const t = WARNING_TYPES[key];
              return (
                <button key={key} onClick={() => onSelect(key)}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:'12px', padding:'12px 14px', background:C.cream, border:`1px solid ${C.border}`, borderRadius:'10px', cursor:'pointer', textAlign:'left', transition:'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = C.white; e.currentTarget.style.borderColor = C.navy; }}
                  onMouseLeave={e => { e.currentTarget.style.background = C.cream; e.currentTarget.style.borderColor = C.border; }}>
                  <span style={{ fontSize:'1.4rem', flexShrink:0 }}>{t.icon}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:'13px', color:C.charcoal }}>{t.label}</div>
                    <div style={{ fontSize:'11px', color:C.mid, marginTop:'2px' }}>{t.description}</div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Formal */}
          <div>
            <div style={{ fontSize:'11px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'8px' }}>Formal — Dossier</div>
            <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
              {formal.map(key => {
                const t = WARNING_TYPES[key];
                return (
                  <button key={key} onClick={() => onSelect(key)}
                    style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px 14px', background:t.bg, border:`1px solid ${t.border}44`, borderRadius:'10px', cursor:'pointer', textAlign:'left', transition:'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = t.border; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = t.border + '44'; }}>
                    <span style={{ fontSize:'1.4rem', flexShrink:0 }}>{t.icon}</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:'13px', color:t.color }}>{t.label}</div>
                      <div style={{ fontSize:'11px', color:C.mid, marginTop:'2px' }}>{t.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Legal note */}
          <div style={{ padding:'10px 12px', background:C.amberLight, borderRadius:'8px', border:`1px solid ${C.amber}44`, fontSize:'11px', color:C.amber, lineHeight:1.5 }}>
            <strong>NL law:</strong> Verbal → Written → Final → PIP is the expected escalation ladder for dismissal on performance/conduct grounds. Always document before dismissing.
          </div>

        </div>
      </div>
    </div>
  );
}

// ── Warning form ──────────────────────────────────────────────────────
function WarningForm({ type, member, currentUser, onClose, onSaved }) {
  const conf = WARNING_TYPES[type];
  const isFormal = !conf.internal;

  const [date,                setDate]               = useState(new Date().toISOString().split('T')[0]);
  const [incidentDate,        setIncidentDate]        = useState('');
  const [severity,            setSeverity]            = useState('minor');
  const [description,         setDescription]         = useState('');
  const [expectation,         setExpectation]         = useState('');
  const [employeeResponse,    setEmployeeResponse]    = useState('');
  const [improvementDays,     setImprovementDays]     = useState(30);
  const [checkinDate,         setCheckinDate]         = useState('');
  const [supportOffered,      setSupportOffered]      = useState('');
  const [outcomeResult,       setOutcomeResult]       = useState('');
  const [saving,              setSaving]              = useState(false);
  const [saved,               setSaved]               = useState(false);
  const [emailSending,        setEmailSending]        = useState(false);
  const [link,                setLink]                = useState('');
  const [copied,              setCopied]              = useState(false);
  const [refusedWitness,      setRefusedWitness]      = useState('');
  const [showRefused,         setShowRefused]         = useState(false);

  async function handleSave() {
    if (!description.trim()) { alert('Please enter a description.'); return; }
    if (type === 'outcome' && !outcomeResult) { alert('Please select an outcome.'); return; }
    setSaving(true);

    const token = isFormal ? genToken() : null;
    const payload = {
      team_member_id:          member.id,
      type,
      date,
      incident_date:           incidentDate || null,
      issued_by:               currentUser?.name || 'Manager',
      severity:                type === 'informal' ? severity : null,
      description:             description.trim(),
      expectation:             expectation.trim() || null,
      employee_response:       employeeResponse.trim() || null,
      improvement_period_days: type === 'pip' ? (parseInt(improvementDays) || null) : null,
      checkin_date:            type === 'pip' ? (checkinDate || null) : null,
      support_offered:         type === 'pip' ? (supportOffered.trim() || null) : null,
      outcome_result:          type === 'outcome' ? outcomeResult : null,
      internal_only:           conf.internal,
      token,
    };

    const { error } = await supabase.from('warnings').insert(payload);
    setSaving(false);
    if (error) { alert('Error saving: ' + error.message); return; }
    setSaved(true);
    if (onSaved) onSaved();

    if (isFormal && token) {
      const warningUrl = `${window.location.origin}/warning/${token}`;
      setLink(warningUrl);
    }
  }

  async function handleSendEmail() {
    if (!member.email) { alert('No email address on file for this team member.'); return; }
    if (!link) return;
    setEmailSending(true);
    const { error } = await supabase.functions.invoke('send-warning-email', {
      body: {
        to:       member.email,
        name:     member.name,
        type:     conf.label,
        warningUrl: link,
        sentBy:   currentUser?.name || 'Manager',
      },
    }).catch(() => ({ error: true }));
    if (error) {
      alert('Email function not yet set up — copy the link below and share manually.');
    } else {
      await supabase.from('warnings')
        .update({ email_sent_at: new Date().toISOString(), email_sent_to: member.email })
        .eq('token', link.split('/warning/')[1]);
    }
    setEmailSending(false);
  }

  async function copyLink() {
    await navigator.clipboard.writeText(link);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  }

  async function handleRefused() {
    if (!refusedWitness.trim()) { alert('Please enter the name of the witness.'); return; }
    await supabase.from('warnings')
      .update({ confirmation_refused: true, refused_witnessed_by: refusedWitness.trim() })
      .eq('token', link.split('/warning/')[1]);
    setShowRefused(false);
    alert('Refusal logged with witness: ' + refusedWitness);
  }

  // ── Saved state ──
  if (saved) return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:800, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:C.white, borderRadius:'16px', width:'100%', maxWidth:'440px', boxShadow:'0 24px 64px rgba(0,0,0,0.22)', overflow:'hidden' }}>
        <div style={{ padding:'18px 24px', background:conf.internal ? C.navy : C.red, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontWeight:700, fontSize:'15px', color:C.white }}>{conf.icon} {conf.label} Saved</div>
            <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.7)', marginTop:'2px' }}>{member.name}</div>
          </div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.12)', border:'none', borderRadius:'8px', cursor:'pointer', color:C.white, padding:'6px', display:'flex' }}><X size={18}/></button>
        </div>
        <div style={{ padding:'24px', display:'flex', flexDirection:'column', gap:'14px' }}>
          <div style={{ textAlign:'center', padding:'8px 0' }}>
            <CheckCircle size={40} color={C.green} style={{ marginBottom:'10px' }}/>
            <div style={{ fontWeight:700, fontSize:'15px', color:C.charcoal, marginBottom:'4px' }}>Logged to dossier</div>
            <div style={{ fontSize:'13px', color:C.mid }}>Saved to {member.name}'s conduct log.</div>
          </div>

          {isFormal && link && (
            <>
              <div style={{ padding:'12px 14px', background:C.amberLight, border:`1px solid ${C.amber}44`, borderRadius:'8px', fontSize:'12px', color:C.amber, lineHeight:1.5 }}>
                Send the confirmation link to the employee so they can acknowledge receipt. Their confirmation is timestamped and saved automatically.
              </div>

              {/* Email button */}
              {member.email && (
                <button onClick={handleSendEmail} disabled={emailSending}
                  style={{ padding:'10px 16px', background:emailSending ? C.border : C.navy, color:C.white, border:'none', borderRadius:'9px', cursor:emailSending?'not-allowed':'pointer', fontWeight:600, fontSize:'13px', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
                  <Send size={14}/>{emailSending ? 'Sending…' : `Email confirmation link to ${member.name}`}
                </button>
              )}

              {/* Copy link */}
              <div style={{ background:C.cream, borderRadius:'10px', padding:'12px', border:`1px solid ${C.border}` }}>
                <div style={{ fontSize:'11px', fontWeight:700, color:C.mid, marginBottom:'8px', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                  {member.email ? 'Or copy link manually' : 'No email on file — share this link'}
                </div>
                <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
                  <div style={{ flex:1, fontSize:'12px', color:C.charcoal, background:C.white, border:`1px solid ${C.border}`, borderRadius:'6px', padding:'7px 10px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{link}</div>
                  <button onClick={copyLink}
                    style={{ padding:'7px 12px', background:copied?C.green:C.navy, color:C.white, border:'none', borderRadius:'7px', cursor:'pointer', fontSize:'12px', fontWeight:600, whiteSpace:'nowrap', display:'flex', alignItems:'center', gap:'5px', flexShrink:0 }}>
                    {copied ? <><Check size={12}/>Copied!</> : <><Copy size={12}/>Copy</>}
                  </button>
                </div>
              </div>

              {/* PDF stub */}
              <button disabled
                style={{ padding:'10px 16px', background:C.cream, color:C.mid, border:`1px solid ${C.border}`, borderRadius:'9px', cursor:'not-allowed', fontWeight:600, fontSize:'13px', opacity:0.7 }}>
                📄 Download PDF (coming soon)
              </button>

              {/* Refused to sign */}
              {!showRefused ? (
                <button onClick={() => setShowRefused(true)}
                  style={{ padding:'8px', background:'none', color:C.mid, border:`1px solid ${C.border}`, borderRadius:'8px', cursor:'pointer', fontSize:'12px' }}>
                  Employee refused to acknowledge? Log refusal
                </button>
              ) : (
                <div style={{ background:C.redLight, border:`1px solid ${C.red}44`, borderRadius:'10px', padding:'12px' }}>
                  <div style={{ fontSize:'12px', fontWeight:700, color:C.red, marginBottom:'8px' }}>Log refusal to acknowledge</div>
                  <div style={{ fontSize:'11px', color:C.mid, marginBottom:'8px' }}>Enter the name of the manager/witness present when refusal occurred.</div>
                  <input value={refusedWitness} onChange={e => setRefusedWitness(e.target.value)} placeholder="Witness name" style={{ ...inp, marginBottom:'8px' }}/>
                  <div style={{ display:'flex', gap:'8px' }}>
                    <button onClick={handleRefused} style={{ flex:1, padding:'8px', background:C.red, color:C.white, border:'none', borderRadius:'7px', cursor:'pointer', fontSize:'12px', fontWeight:600 }}>Log Refusal</button>
                    <button onClick={() => setShowRefused(false)} style={{ padding:'8px 14px', background:'none', border:`1px solid ${C.border}`, borderRadius:'7px', cursor:'pointer', fontSize:'12px', color:C.mid }}>Cancel</button>
                  </div>
                </div>
              )}
            </>
          )}

          <button onClick={onClose}
            style={{ padding:'10px', background:C.cream, color:C.navy, border:`1px solid ${C.border}`, borderRadius:'9px', fontSize:'13px', fontWeight:600, cursor:'pointer' }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );

  // ── Form ──
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:800, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:C.white, borderRadius:'16px', width:'100%', maxWidth:'560px', maxHeight:'92vh', overflowY:'auto', boxShadow:'0 24px 64px rgba(0,0,0,0.22)', display:'flex', flexDirection:'column' }}>

        {/* Header */}
        <div style={{ padding:'18px 24px', background: conf.internal ? C.navy : C.red, borderRadius:'16px 16px 0 0', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:10 }}>
          <div>
            <div style={{ fontWeight:700, fontSize:'15px', color:C.white }}>{conf.icon} {conf.label}</div>
            <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.7)', marginTop:'2px' }}>{member.name} · {member.venue}</div>
          </div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.12)', border:'none', borderRadius:'8px', cursor:'pointer', color:C.white, padding:'6px', display:'flex' }}><X size={18}/></button>
        </div>

        <div style={{ padding:'24px', display:'flex', flexDirection:'column', gap:'16px' }}>

          {/* Meta row */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
            <div>
              <label style={lbl}>Date of Entry</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inp}/>
            </div>
            {type !== 'outcome' && (
              <div>
                <label style={lbl}>Date of Incident <span style={{ fontWeight:400, color:C.mid }}>(if different)</span></label>
                <input type="date" value={incidentDate} onChange={e => setIncidentDate(e.target.value)} style={inp}/>
              </div>
            )}
            <div>
              <label style={lbl}>Logged by</label>
              <input value={currentUser?.name || 'Manager'} readOnly style={{ ...inp, background:C.cream, color:C.mid }}/>
            </div>
          </div>

          {/* Severity — informal only */}
          {type === 'informal' && (
            <div>
              <label style={lbl}>Severity</label>
              <div style={{ display:'flex', gap:'8px' }}>
                {Object.entries(SEVERITY_CONFIG).map(([key, s]) => (
                  <button key={key} onClick={() => setSeverity(key)}
                    style={{ flex:1, padding:'8px', border:`1.5px solid ${severity === key ? s.color : C.border}`, borderRadius:'8px', cursor:'pointer', fontSize:'12px', fontWeight:severity === key ? 700 : 400, background:severity === key ? s.bg : C.white, color:severity === key ? s.color : C.mid, transition:'all 0.15s' }}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <label style={lbl}>
              {type === 'outcome' ? 'Summary' : type === 'pip' ? 'Performance issues to address' : 'Description of incident / behaviour'}
            </label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4}
              style={{ ...inp, resize:'vertical', fontFamily:'inherit', lineHeight:1.6 }}
              placeholder={type === 'informal' ? 'What was observed...' : type === 'outcome' ? 'Summary of the improvement period and result...' : 'What happened, when, who was involved...'}
            />
          </div>

          {/* Expectation — formal types except outcome */}
          {isFormal && type !== 'outcome' && type !== 'pip' && (
            <div>
              <label style={lbl}>Expected improvement / behaviour</label>
              <textarea value={expectation} onChange={e => setExpectation(e.target.value)} rows={3}
                style={{ ...inp, resize:'vertical', fontFamily:'inherit', lineHeight:1.6 }}
                placeholder="What must change and by when..."
              />
            </div>
          )}

          {/* PIP-specific fields */}
          {type === 'pip' && (
            <>
              <div>
                <label style={lbl}>Goals / expected improvements</label>
                <textarea value={expectation} onChange={e => setExpectation(e.target.value)} rows={3}
                  style={{ ...inp, resize:'vertical', fontFamily:'inherit', lineHeight:1.6 }}
                  placeholder="Specific measurable goals for the improvement period..."
                />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                <div>
                  <label style={lbl}>Improvement period (days)</label>
                  <input type="number" min="7" max="180" value={improvementDays} onChange={e => setImprovementDays(e.target.value)} style={inp}/>
                </div>
                <div>
                  <label style={lbl}>Check-in date</label>
                  <input type="date" value={checkinDate} onChange={e => setCheckinDate(e.target.value)} style={inp}/>
                </div>
              </div>
              <div>
                <label style={lbl}>Support offered <span style={{ fontWeight:400, color:C.mid }}>(optional)</span></label>
                <input value={supportOffered} onChange={e => setSupportOffered(e.target.value)}
                  placeholder="e.g. Additional training, reduced section, weekly 1-on-1s"
                  style={inp}/>
              </div>
            </>
          )}

          {/* Outcome result */}
          {type === 'outcome' && (
            <div>
              <label style={lbl}>Outcome</label>
              <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                {OUTCOME_OPTIONS.map(o => (
                  <button key={o.value} onClick={() => setOutcomeResult(o.value)}
                    style={{ padding:'10px 14px', border:`1.5px solid ${outcomeResult === o.value ? C.navy : C.border}`, borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:outcomeResult === o.value ? 700 : 400, background:outcomeResult === o.value ? C.navy : C.white, color:outcomeResult === o.value ? C.white : C.charcoal, textAlign:'left', transition:'all 0.15s' }}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Employee response — formal non-outcome */}
          {isFormal && type !== 'outcome' && (
            <div>
              <label style={lbl}>Employee response / comments <span style={{ fontWeight:400, color:C.mid }}>(optional)</span></label>
              <textarea value={employeeResponse} onChange={e => setEmployeeResponse(e.target.value)} rows={2}
                style={{ ...inp, resize:'vertical', fontFamily:'inherit', lineHeight:1.6 }}
                placeholder="Employee's verbal response when warned..."
              />
            </div>
          )}

          {/* Legal note for formal */}
          {isFormal && type !== 'outcome' && (
            <div style={{ padding:'10px 12px', background:C.amberLight, border:`1px solid ${C.amber}44`, borderRadius:'8px', fontSize:'11px', color:C.amber, lineHeight:1.5 }}>
              After saving, you'll be able to email a confirmation link to the employee and download a PDF for physical signature.
            </div>
          )}

          {/* Save */}
          <div style={{ display:'flex', gap:'10px' }}>
            <button onClick={handleSave} disabled={saving || !description.trim()}
              style={{ flex:1, padding:'12px', background:saving || !description.trim() ? C.border : conf.internal ? C.navy : C.red, color:C.white, border:'none', borderRadius:'9px', cursor:saving || !description.trim() ? 'not-allowed' : 'pointer', fontWeight:700, fontSize:'14px' }}>
              {saving ? 'Saving…' : `Save ${conf.label}`}
            </button>
            <button onClick={onClose}
              style={{ padding:'12px 16px', border:`1px solid ${C.border}`, borderRadius:'9px', background:C.white, cursor:'pointer', fontSize:'13px', color:C.mid }}>
              Cancel
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

// ── Recognition form ──────────────────────────────────────────────────
function RecognitionForm({ type, member, currentUser, onClose, onSaved }) {
  const conf = RECOGNITION_TYPES[type];
  const now  = new Date();

  const [date,        setDate]        = useState(now.toISOString().split('T')[0]);
  const [description, setDescription] = useState('');
  const [category,    setCategory]    = useState('');
  // TM of the month: month picker (YYYY-MM), defaults to current month
  const [awardMonth,  setAwardMonth]  = useState(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');

  async function handleSave() {
    if (!description.trim()) { setError('Please add a description.'); return; }
    setSaving(true);
    setError('');

    // TM of the Month: check no winner already exists for that month
    if (type === 'tm_of_month') {
      const [year, month] = awardMonth.split('-').map(Number);
      const { data: existing } = await supabase
        .from('warnings')
        .select('id, team_member_id')
        .eq('entry_category', 'recognition')
        .eq('type', 'tm_of_month')
        .eq('award_month', month)
        .eq('award_year', year);

      if (existing && existing.length > 0) {
        setSaving(false);
        setError(`A TM of the Month winner already exists for ${awardMonth}. Delete it first to reassign.`);
        return;
      }
    }

    const [awardY, awardM] = type === 'tm_of_month' ? awardMonth.split('-').map(Number) : [null, null];

    const payload = {
      team_member_id: member.id,
      type,
      entry_category: 'recognition',
      date,
      issued_by:      currentUser?.name || 'Manager',
      description:    description.trim(),
      internal_only:  true,
      severity:       category || null,   // reuse severity column for recognition category
      tm_of_month:    type === 'tm_of_month',
      award_month:    awardM,
      award_year:     awardY,
      token:          null,
    };

    const { error: dbErr } = await supabase.from('warnings').insert(payload);
    setSaving(false);
    if (dbErr) { setError('Error saving: ' + dbErr.message); return; }
    if (onSaved) onSaved();
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:800, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:C.white, borderRadius:'16px', width:'100%', maxWidth:'480px', maxHeight:'92vh', overflowY:'auto', boxShadow:'0 24px 64px rgba(0,0,0,0.22)', display:'flex', flexDirection:'column' }}>

        {/* Header */}
        <div style={{ padding:'18px 24px', background: type === 'tm_of_month' ? '#B8860B' : C.navy, borderRadius:'16px 16px 0 0', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:10 }}>
          <div>
            <div style={{ fontWeight:700, fontSize:'15px', color:C.white }}>{conf.icon} {conf.label}</div>
            <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.75)', marginTop:'2px' }}>{member.name} · {member.venue}</div>
          </div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.12)', border:'none', borderRadius:'8px', cursor:'pointer', color:C.white, padding:'6px', display:'flex' }}><X size={18}/></button>
        </div>

        <div style={{ padding:'24px', display:'flex', flexDirection:'column', gap:'16px' }}>

          {/* TM of the Month — month picker */}
          {type === 'tm_of_month' && (
            <div style={{ padding:'12px 14px', background:C.amberLight, border:`1px solid ${C.amber}44`, borderRadius:'10px' }}>
              <label style={{ fontSize:'10px', fontWeight:700, color:C.amber, textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px' }}>Award Month</label>
              <input
                type="month"
                value={awardMonth}
                onChange={e => setAwardMonth(e.target.value)}
                style={{ ...inp, fontWeight:700, fontSize:'14px' }}
              />
              <div style={{ fontSize:'11px', color:C.amber, marginTop:'6px' }}>One winner per calendar month, company-wide. €100 cash prize.</div>
            </div>
          )}

          {/* Date logged */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
            <div>
              <label style={{ fontSize:'10px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'4px' }}>Date logged</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inp}/>
            </div>
            <div>
              <label style={{ fontSize:'10px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'4px' }}>Logged by</label>
              <input value={currentUser?.name || 'Manager'} readOnly style={{ ...inp, background:C.cream, color:C.mid }}/>
            </div>
          </div>

          {/* Category — not for tm_of_month */}
          {type !== 'tm_of_month' && (
            <div>
              <label style={{ fontSize:'10px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px' }}>Category <span style={{ fontWeight:400 }}>(optional)</span></label>
              <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
                {RECOGNITION_CATEGORIES.map(c => (
                  <button key={c.value} onClick={() => setCategory(p => p === c.value ? '' : c.value)}
                    style={{ padding:'5px 12px', borderRadius:'20px', fontSize:'12px', fontWeight:600, cursor:'pointer', border:`1.5px solid ${category === c.value ? conf.color : C.border}`, background:category === c.value ? conf.bg : C.white, color:category === c.value ? conf.color : C.mid, transition:'all 0.15s' }}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <label style={{ fontSize:'10px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'4px' }}>
              {type === 'tm_of_month' ? 'Why they deserve it' : 'What happened / what they did'}
            </label>
            <textarea value={description} onChange={e => { setDescription(e.target.value); setError(''); }} rows={4}
              style={{ ...inp, resize:'vertical', fontFamily:'inherit', lineHeight:1.6 }}
              placeholder={type === 'tm_of_month' ? 'Describe their contribution this month…' : type === 'commendation' ? 'Describe the contribution or behaviour…' : 'Great service on table 12, stayed late to help with close, customer specifically asked for them…'}
            />
          </div>

          {error && (
            <div style={{ padding:'10px 12px', background:C.redLight, border:`1px solid ${C.red}44`, borderRadius:'8px', fontSize:'12px', color:C.red }}>
              {error}
            </div>
          )}

          {/* Save */}
          <div style={{ display:'flex', gap:'10px' }}>
            <button onClick={handleSave} disabled={saving || !description.trim()}
              style={{ flex:1, padding:'12px', background:saving || !description.trim() ? C.border : type === 'tm_of_month' ? '#B8860B' : conf.color, color:C.white, border:'none', borderRadius:'9px', cursor:saving || !description.trim() ? 'not-allowed':'pointer', fontWeight:700, fontSize:'14px' }}>
              {saving ? 'Saving…' : `Save ${conf.label}`}
            </button>
            <button onClick={onClose}
              style={{ padding:'12px 16px', border:`1px solid ${C.border}`, borderRadius:'9px', background:C.white, cursor:'pointer', fontSize:'13px', color:C.mid }}>
              Cancel
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

// ── History list ──────────────────────────────────────────────────────
function WarningHistory({ member, currentUser, onClose, onNew }) {
  const [warnings,     setWarnings]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [expanded,     setExpanded]     = useState(null);
  const [sendingEmail, setSendingEmail] = useState({});
  const [copied,       setCopied]       = useState({});
  const [refusedOpen,  setRefusedOpen]  = useState({});
  const [refusedWitness, setRefusedWitness] = useState({});

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from('warnings')
        .select('*')
        .eq('team_member_id', member.id)
        .order('date', { ascending: false });
      setWarnings(data || []);
      setLoading(false);
    }
    load();
  }, [member.id]);

  const conductEntries     = warnings.filter(w => !w.entry_category || w.entry_category === 'conduct');
  const recognitionEntries = warnings.filter(w => w.entry_category === 'recognition');
  const formalCount        = conductEntries.filter(w => !w.internal_only).length;

  async function handleSendEmail(w) {
    if (!member.email) { alert('No email address on file for this team member.'); return; }
    setSendingEmail(p => ({ ...p, [w.id]: true }));
    const warningUrl = `${window.location.origin}/warning/${w.token}`;
    const conf = WARNING_TYPES[w.type] || WARNING_TYPES.verbal;
    const { error } = await supabase.functions.invoke('send-warning-email', {
      body: { to: member.email, name: member.name, type: conf.label, warningUrl, sentBy: currentUser?.name || 'Manager' },
    }).catch(() => ({ error: true }));
    if (error) {
      alert('Email function not yet set up — copy the link and share manually.');
    } else {
      const now = new Date().toISOString();
      await supabase.from('warnings').update({ email_sent_at: now, email_sent_to: member.email }).eq('id', w.id);
      setWarnings(p => p.map(x => x.id === w.id ? { ...x, email_sent_at: now, email_sent_to: member.email } : x));
    }
    setSendingEmail(p => ({ ...p, [w.id]: false }));
  }

  async function handleCopyLink(w) {
    const url = `${window.location.origin}/warning/${w.token}`;
    await navigator.clipboard.writeText(url);
    setCopied(p => ({ ...p, [w.id]: true }));
    setTimeout(() => setCopied(p => ({ ...p, [w.id]: false })), 2000);
  }

  async function handleLogRefusal(w) {
    const witness = refusedWitness[w.id]?.trim();
    if (!witness) { alert('Please enter the witness name.'); return; }
    await supabase.from('warnings').update({ confirmation_refused: true, refused_witnessed_by: witness }).eq('id', w.id);
    setWarnings(p => p.map(x => x.id === w.id ? { ...x, confirmation_refused: true, refused_witnessed_by: witness } : x));
    setRefusedOpen(p => ({ ...p, [w.id]: false }));
  }

  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:700, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:C.white, borderRadius:'16px', width:'100%', maxWidth:'620px', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 24px 64px rgba(0,0,0,0.22)', display:'flex', flexDirection:'column' }}>

        {/* Header */}
        <div style={{ padding:'18px 24px', background:C.navy, borderRadius:'16px 16px 0 0', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:10 }}>
          <div>
            <div style={{ fontWeight:700, fontSize:'15px', color:C.white }}>Performance Log</div>
            <div style={{ fontSize:'12px', color:C.skyBlue, marginTop:'2px', display:'flex', alignItems:'center', gap:'8px' }}>
              {member.name}
              {formalCount > 0 && <span style={{ padding:'1px 8px', borderRadius:'20px', background:'rgba(220,53,69,0.3)', fontSize:'11px', fontWeight:700, color:'#ffcccc' }}>{formalCount} ⚠ formal</span>}
              {recognitionEntries.length > 0 && <span style={{ padding:'1px 8px', borderRadius:'20px', background:'rgba(126,165,148,0.3)', fontSize:'11px', fontWeight:700, color:'#b8f5d8' }}>{recognitionEntries.length} ✦ recognition</span>}
            </div>
          </div>
          <div style={{ display:'flex', gap:'6px', alignItems:'center' }}>
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.12)', border:'none', borderRadius:'8px', cursor:'pointer', color:C.white, padding:'6px', display:'flex', marginLeft:'2px' }}><X size={18}/></button>
          </div>
        </div>

        <div style={{ padding:'20px 24px' }}>
          {loading && <p style={{ color:C.mid, fontSize:'13px' }}>Loading…</p>}

          {!loading && warnings.length === 0 && (
            <div style={{ textAlign:'center', padding:'40px 0' }}>
              <div style={{ fontSize:'2.5rem', marginBottom:'12px' }}>📋</div>
              <p style={{ fontWeight:700, fontSize:'15px', color:C.charcoal, margin:'0 0 6px' }}>No entries yet</p>
              <p style={{ fontSize:'13px', color:C.mid, margin:'0 0 20px' }}>Log conduct issues or recognise great work for {member.name}.</p>
              <div style={{ display:'flex', gap:'8px', justifyContent:'center' }}>
                <button onClick={() => onNew('conduct')}
                  style={{ padding:'10px 20px', background:C.redLight, color:C.red, border:`1.5px solid ${C.red}44`, borderRadius:'9px', cursor:'pointer', fontWeight:600, fontSize:'13px' }}>
                  + Conduct Log
                </button>
                <button onClick={() => onNew('recognition')}
                  style={{ padding:'10px 20px', background:C.greenLight, color:C.green, border:`1.5px solid ${C.green}44`, borderRadius:'9px', cursor:'pointer', fontWeight:600, fontSize:'13px' }}>
                  + Recognition
                </button>
              </div>
            </div>
          )}

          {!loading && warnings.length > 0 && (
            <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
              {warnings.map(w => {
                const isRecognition = w.entry_category === 'recognition';
                const conf    = isRecognition ? (RECOGNITION_TYPES[w.type] || RECOGNITION_TYPES.kudos) : (WARNING_TYPES[w.type] || WARNING_TYPES.informal);
                const isTMOM  = w.tm_of_month;
                const isOpen  = expanded === w.id;
                const confirmed = !!w.confirmed_at;
                const refused   = !!w.confirmation_refused;
                const pending   = !conf.internal && !confirmed && !refused && !!w.token;
                const recogCat  = isRecognition && w.severity ? RECOGNITION_CATEGORIES.find(c => c.value === w.severity) : null;

                return (
                  <div key={w.id} style={{
                    border:`1px solid ${isTMOM ? C.amber : conf.border || conf.color}44`,
                    borderRadius:'12px', overflow:'hidden',
                    borderLeft:`3px solid ${isTMOM ? C.amber : conf.color}`,
                    boxShadow: isTMOM ? `0 2px 12px ${C.amber}33` : 'none',
                  }}>

                    {/* TM of Month gold banner */}
                    {isTMOM && (
                      <div style={{ padding:'6px 16px', background:`linear-gradient(90deg, ${C.amber}22, ${C.amber}08)`, borderBottom:`1px solid ${C.amber}33`, display:'flex', alignItems:'center', gap:'6px' }}>
                        <span style={{ fontSize:'14px' }}>🏆</span>
                        <span style={{ fontSize:'11px', fontWeight:700, color:C.amber }}>
                          TM of the Month — {w.award_month && w.award_year ? `${MONTH_NAMES[w.award_month-1]} ${w.award_year}` : fmt(w.date)}
                        </span>
                        <span style={{ marginLeft:'auto', fontSize:'11px', fontWeight:700, color:C.amber }}>€100</span>
                      </div>
                    )}

                    {/* Row */}
                    <button onClick={() => setExpanded(isOpen ? null : w.id)}
                      style={{ width:'100%', display:'flex', alignItems:'center', gap:'12px', padding:'12px 16px', background: isOpen ? (isTMOM ? C.amberLight : conf.bg) : C.white, border:'none', cursor:'pointer', textAlign:'left' }}>
                      <span style={{ fontSize:'1.2rem', flexShrink:0 }}>{conf.icon}</span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap', marginBottom:'2px' }}>
                          <span style={{ fontWeight:700, fontSize:'13px', color: isTMOM ? C.amber : conf.color }}>{conf.label}</span>
                          {recogCat && (
                            <span style={{ padding:'1px 7px', borderRadius:'20px', fontSize:'10px', fontWeight:700, background:conf.bg, color:conf.color }}>
                              {recogCat.label}
                            </span>
                          )}
                          {!isRecognition && w.severity && (
                            <span style={{ padding:'1px 7px', borderRadius:'20px', fontSize:'10px', fontWeight:700, background:SEVERITY_CONFIG[w.severity]?.bg, color:SEVERITY_CONFIG[w.severity]?.color }}>
                              {SEVERITY_CONFIG[w.severity]?.label}
                            </span>
                          )}
                          {confirmed && <span style={{ fontSize:'10px', color:C.green, fontWeight:700 }}>✓ TM confirmed</span>}
                          {refused   && <span style={{ fontSize:'10px', color:C.red,   fontWeight:700 }}>✗ Refused</span>}
                          {pending   && <span style={{ fontSize:'10px', color:C.amber, fontWeight:700 }}>⏳ Awaiting</span>}
                        </div>
                        <div style={{ fontSize:'11px', color:C.mid }}>{fmt(w.date)} · {w.issued_by}</div>
                      </div>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink:0, transform:isOpen?'rotate(180deg)':'rotate(0)', transition:'transform 0.2s' }}>
                        <path d="M2.5 5L7 9.5L11.5 5" stroke={C.mid} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>

                    {/* Expanded detail */}
                    {isOpen && (
                      <div style={{ padding:'14px 16px', borderTop:`1px solid ${(conf.border || conf.color)}33`, background: isTMOM ? C.amberLight : conf.bg, display:'flex', flexDirection:'column', gap:'10px' }}>
                        {w.incident_date && (
                          <div style={{ fontSize:'11px', color:C.mid }}>Incident date: {fmt(w.incident_date)}</div>
                        )}
                        <div>
                          <div style={{ fontSize:'10px', fontWeight:700, color:C.mid, textTransform:'uppercase', marginBottom:'4px' }}>
                            {isRecognition ? 'Details' : 'Description'}
                          </div>
                          <div style={{ fontSize:'13px', color:C.charcoal, lineHeight:1.6, whiteSpace:'pre-wrap' }}>{w.description}</div>
                        </div>
                        {!isRecognition && w.expectation && (
                          <div>
                            <div style={{ fontSize:'10px', fontWeight:700, color:C.mid, textTransform:'uppercase', marginBottom:'4px' }}>Expected improvement</div>
                            <div style={{ fontSize:'13px', color:C.charcoal, lineHeight:1.6, whiteSpace:'pre-wrap' }}>{w.expectation}</div>
                          </div>
                        )}
                        {!isRecognition && w.improvement_period_days && (
                          <div style={{ display:'flex', gap:'16px', flexWrap:'wrap' }}>
                            <div>
                              <div style={{ fontSize:'10px', fontWeight:700, color:C.mid, textTransform:'uppercase', marginBottom:'2px' }}>Improvement period</div>
                              <div style={{ fontSize:'13px', color:C.charcoal }}>{w.improvement_period_days} days</div>
                            </div>
                            {w.checkin_date && (
                              <div>
                                <div style={{ fontSize:'10px', fontWeight:700, color:C.mid, textTransform:'uppercase', marginBottom:'2px' }}>Check-in date</div>
                                <div style={{ fontSize:'13px', color:C.charcoal }}>{fmt(w.checkin_date)}</div>
                              </div>
                            )}
                          </div>
                        )}
                        {!isRecognition && w.support_offered && (
                          <div>
                            <div style={{ fontSize:'10px', fontWeight:700, color:C.mid, textTransform:'uppercase', marginBottom:'4px' }}>Support offered</div>
                            <div style={{ fontSize:'13px', color:C.charcoal }}>{w.support_offered}</div>
                          </div>
                        )}
                        {!isRecognition && w.employee_response && (
                          <div>
                            <div style={{ fontSize:'10px', fontWeight:700, color:C.mid, textTransform:'uppercase', marginBottom:'4px' }}>Employee response</div>
                            <div style={{ fontSize:'13px', color:C.charcoal, fontStyle:'italic', lineHeight:1.6 }}>"{w.employee_response}"</div>
                          </div>
                        )}
                        {!isRecognition && w.outcome_result && (
                          <div>
                            <div style={{ fontSize:'10px', fontWeight:700, color:C.mid, textTransform:'uppercase', marginBottom:'4px' }}>Outcome</div>
                            <div style={{ fontSize:'13px', color:C.charcoal }}>{OUTCOME_OPTIONS.find(o => o.value === w.outcome_result)?.label || w.outcome_result}</div>
                          </div>
                        )}
                        {/* Conduct confirmation flow — unchanged */}
                        {!isRecognition && !conf.internal && w.token && (
                          <div style={{ paddingTop:'10px', borderTop:`1px solid ${(conf.border || conf.color)}33`, display:'flex', flexDirection:'column', gap:'8px' }}>
                            <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                              {confirmed && <span style={{ padding:'3px 10px', borderRadius:'20px', fontSize:'11px', fontWeight:700, background:C.greenLight, color:C.green }}>✓ TM confirmed receipt {fmt(w.confirmed_at)}</span>}
                              {refused   && <span style={{ padding:'3px 10px', borderRadius:'20px', fontSize:'11px', fontWeight:700, background:C.redLight,   color:C.red   }}>✗ Refused — witnessed by {w.refused_witnessed_by}</span>}
                              {pending   && <span style={{ padding:'3px 10px', borderRadius:'20px', fontSize:'11px', fontWeight:700, background:C.amberLight, color:C.amber }}>⏳ Awaiting confirmation</span>}
                              {w.email_sent_at && <span style={{ padding:'3px 10px', borderRadius:'20px', fontSize:'11px', fontWeight:600, background:C.skyBlueLight, color:C.skyText }}>📧 Emailed {fmt(w.email_sent_at)}</span>}
                            </div>
                            {!confirmed && !refused && (
                              <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                                {member.email && (
                                  <button onClick={() => handleSendEmail(w)} disabled={sendingEmail[w.id]}
                                    style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'6px', padding:'8px 14px', background:sendingEmail[w.id] ? C.border : C.navy, color:C.white, border:'none', borderRadius:'8px', cursor:sendingEmail[w.id] ? 'not-allowed':'pointer', fontSize:'12px', fontWeight:600 }}>
                                    <Send size={13}/>{sendingEmail[w.id] ? 'Sending…' : `${w.email_sent_at ? 'Resend' : 'Send'} confirmation link to ${member.name}`}
                                  </button>
                                )}
                                <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
                                  <div style={{ flex:1, fontSize:'11px', color:C.mid, background:C.white, border:`1px solid ${C.border}`, borderRadius:'6px', padding:'6px 10px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                    {`${window.location.origin}/warning/${w.token}`}
                                  </div>
                                  <button onClick={() => handleCopyLink(w)}
                                    style={{ padding:'6px 12px', background:copied[w.id] ? C.green : C.cream, color:copied[w.id] ? C.white : C.navy, border:`1px solid ${C.border}`, borderRadius:'7px', cursor:'pointer', fontSize:'11px', fontWeight:600, whiteSpace:'nowrap', display:'flex', alignItems:'center', gap:'5px', flexShrink:0 }}>
                                    {copied[w.id] ? <><Check size={12}/>Copied!</> : <><Copy size={12}/>Copy link</>}
                                  </button>
                                </div>
                                <button disabled style={{ padding:'7px 14px', background:C.cream, color:C.mid, border:`1px solid ${C.border}`, borderRadius:'8px', cursor:'not-allowed', fontSize:'12px', fontWeight:600, opacity:0.6 }}>
                                  📄 Download PDF (coming soon)
                                </button>
                                {!refusedOpen[w.id] ? (
                                  <button onClick={() => setRefusedOpen(p => ({ ...p, [w.id]: true }))}
                                    style={{ padding:'6px', background:'none', color:C.mid, border:`1px solid ${C.border}`, borderRadius:'7px', cursor:'pointer', fontSize:'11px' }}>
                                    Employee refused to acknowledge? Log refusal
                                  </button>
                                ) : (
                                  <div style={{ background:C.redLight, border:`1px solid ${C.red}44`, borderRadius:'9px', padding:'12px' }}>
                                    <div style={{ fontSize:'12px', fontWeight:700, color:C.red, marginBottom:'6px' }}>Log refusal to acknowledge</div>
                                    <div style={{ fontSize:'11px', color:C.mid, marginBottom:'8px' }}>Enter the name of the manager/witness present.</div>
                                    <input value={refusedWitness[w.id] || ''} onChange={e => setRefusedWitness(p => ({ ...p, [w.id]: e.target.value }))} placeholder="Witness name" style={{ ...inp, marginBottom:'8px' }}/>
                                    <div style={{ display:'flex', gap:'8px' }}>
                                      <button onClick={() => handleLogRefusal(w)} style={{ flex:1, padding:'8px', background:C.red, color:C.white, border:'none', borderRadius:'7px', cursor:'pointer', fontSize:'12px', fontWeight:600 }}>Log Refusal</button>
                                      <button onClick={() => setRefusedOpen(p => ({ ...p, [w.id]: false }))} style={{ padding:'8px 14px', background:'none', border:`1px solid ${C.border}`, borderRadius:'7px', cursor:'pointer', fontSize:'12px', color:C.mid }}>Cancel</button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main export — orchestrates views ─────────────────────────────────
export default function WarningConductLog({ member, currentUser, onClose }) {
  const [view,         setView]         = useState('history');
  const [selectedType, setSelectedType] = useState(null);
  const [selectedCat,  setSelectedCat]  = useState('conduct');
  const [refreshKey,   setRefreshKey]   = useState(0);

  function handleNew(tab) { setSelectedCat(tab || 'conduct'); setView('picker'); }
  function handleSelect(type) { setSelectedType(type); setView('form'); }
  function handleSaved() { setRefreshKey(k => k + 1); setView('history'); }

  if (view === 'picker') {
    return <TypePicker onSelect={handleSelect} onClose={onClose} category={selectedCat}/>;
  }

  if (view === 'form' && selectedType) {
    const isRecognition = Object.keys(RECOGNITION_TYPES).includes(selectedType);
    if (isRecognition) {
      return (
        <RecognitionForm
          type={selectedType}
          member={member}
          currentUser={currentUser}
          onClose={onClose}
          onSaved={handleSaved}
        />
      );
    }
    return (
      <WarningForm
        type={selectedType}
        member={member}
        currentUser={currentUser}
        onClose={onClose}
        onSaved={handleSaved}
      />
    );
  }

  return (
    <WarningHistory
      key={refreshKey}
      member={member}
      currentUser={currentUser}
      onClose={onClose}
      onNew={handleNew}
    />
  );
}